<template>
  <div class="flex flex-col h-screen ext-shell text-gray-900">
    <ClientTopBar
      :active-id="activePage"
      @change="setPage"
    />
    <div class="flex-1 flex min-h-0 overflow-hidden">
      <ClientSideNav
        :title="sideNavTitle"
        :items="sideNavItems"
        :active-domain-id="activeDomainId"
        :active-metric-id="activeMetricId"
        :show-export="activePage === 'overview'"
        :exporting="exporting"
        @select="onSideNavSelect"
        @export="onExport"
      />
      <main class="flex-1 overflow-auto ext-page">
        <div class="py-6 px-16" :key="entityKey">
          <!-- Performance tab -->
          <ExternalPerformance
            v-if="activePage === 'performance'"
            :entityid="entityid"
            :selected-domain-id="activeDomainId"
            :selected-metric-id="activeMetricId"
            @domains-loaded="onDomainsLoaded"
            @select-metric="onSelectMetric"
          />

          <!-- Physician Scorecard tab -->
          <ExternalPhysicianScorecard
            v-else-if="activePage === 'scorecard'"
            :entity-id="entityIdNumber"
            @navigate="onNavigate"
          />

          <!-- Attribution tab -->
          <ExternalAttribution
            v-else-if="activePage === 'attribution'"
            :entity-id="entityIdNumber"
            :active-view="activeAttributionView"
          />

          <!-- Quality tab -->
          <ExternalQuality
            v-else-if="activePage === 'quality'"
            :entity-id="entityIdNumber"
          />

          <!-- Coding tab -->
          <ExternalCoding
            v-else-if="activePage === 'coding'"
            :entity-id="entityIdNumber"
          />

          <!-- Utilization tab -->
          <ExternalUtilization
            v-else-if="activePage === 'utilization'"
            :entity-id="entityIdNumber"
            :active-view="activeUtilizationView"
          />

          <!-- Explorer tab -->
          <ExternalExplorer
            v-else-if="activePage === 'explorer'"
            :entity-id="entityIdNumber"
          />

          <!-- Patient tab -->
          <ExternalPatient
            v-else-if="activePage === 'patient'"
            :entity-id="entityIdNumber"
          />

          <!-- Overview tab (CCLF monthly cost report) -->
          <CCLFReport v-else ref="cclfReport" :initial-entity-id="entityIdNumber" :active-tab="activeOverviewTab" />

        </div>
      </main>
    </div>
    <ClientFooter />
  </div>
</template>

<script>
import ClientTopBar from './ClientTopBar.vue';
import ClientSideNav from './ClientSideNav.vue';
import ClientFooter from './ClientFooter.vue';
import ExternalPerformance from './ExternalPerformance.vue';
import CCLFReport from './CCLFReport/CCLFReport.vue';
import ExternalAttribution from './ExternalAttribution.vue';
import ExternalQuality from './ExternalQuality.vue';
import ExternalCoding from './ExternalCoding.vue';
import ExternalPhysicianScorecard from './ExternalPhysicianScorecard.vue';
import ExternalUtilization from './ExternalUtilization.vue'
import ExternalPatient from './ExternalPatient.vue';
import ExternalExplorer from './ExternalExplorer.vue';

export default {
  name: 'ExternalModule',
  components: { ClientTopBar, ClientSideNav, ClientFooter, ExternalPerformance, CCLFReport, ExternalAttribution, ExternalQuality, ExternalCoding, ExternalUtilization, ExternalExplorer, ExternalPatient, ExternalPhysicianScorecard },
  props: {
    entityid: { type: [String, Number], default: null },
  },
  data() {
    return {
      activePage: 'overview',
      performanceDomains: [],
      activeDomainId: 'summary-analysis',
      activeMetricId: null,
      activeAttributionView: 'patient_report',
      activeOverviewTab: 'summary-analysis',
      activeUtilizationView: 'cost-utilization',
      exporting: false,
      entityKey: 0,
    };
  },
  mounted() {
    const tab = this.$route.query.tab
    const view = this.$route.query.view
    if (tab) {
      this.setPage(tab, true)
      if (view) {
        if (tab === 'overview') { this.activeOverviewTab = view; this.activeDomainId = view; }
        else if (tab === 'attribution') { this.activeAttributionView = view; this.activeDomainId = view; }
        else if (tab === 'utilization') { this.activeUtilizationView = view; this.activeDomainId = view; }
        else if (tab === 'performance') {
          const parts = view.split(':')
          if (parts.length === 2) { this.activeDomainId = Number(parts[0]); this.activeMetricId = Number(parts[1]); }
          else { this.activeDomainId = isNaN(Number(view)) ? view : Number(view); }
        }
      }
    }
  },
  watch: {
    entityid() {
      this.entityKey++;
    },
  },
  computed: {
    entityIdNumber() {
      return this.entityid ? Number(this.entityid) : null;
    },
    sideNavItems() {
      if (this.activePage === 'overview') {
        return [
          { id: 'summary-analysis', label: 'Summary' },
          { id: 'patient-report', label: 'Patient Report' },
          { id: 'patient-visits', label: 'Patient Visits' },
          { id: 'awv-summary', label: 'AWV Summary' },
        ];
      }
      if (this.activePage === 'performance') {
        return this.performanceDomains.map(d => ({
          id: d.id,
          label: d.name,
          children: (d.metrics || []).map(m => ({
            id: m.metric_id,
            label: m.metric_name,
          })),
        }));
      }
      if (this.activePage === 'attribution') {
        return [
          { id: 'patient_report', label: 'Patient Report' },
          { id: 'prospects', label: 'Attribution Prospects' },
          { id: 'cost', label: 'Cost Attribution' },
          { id: 'flow', label: 'Attribution Flow' },
        ];
      }
      if (this.activePage === 'utilization') {
        return [
          { id: 'cost-utilization', label: 'Cost & Utilization' },
          { id: 'outpatient-summary', label: 'Outpatient Summary' },
          { id: 'drg-report', label: 'DRG Report' },
          { id: 'hospice', label: 'Hospice' },
          { id: 'therapy', label: 'PT / OT Therapy' },
          { id: 'acsc-cascade', label: 'ACSC Cascade' },
          { id: 'chained_episodes', label: 'Chained Episodes' },
        ];
      }
      if (this.activePage === 'patient') {
        return [];
      }
      return [];
    },
    sideNavTitle() {
      if (this.activePage === 'overview') return 'Overview';
      if (this.activePage === 'performance') return 'Performance';
      if (this.activePage === 'attribution') return 'Attribution';
      if (this.activePage === 'utilization') return 'Utilization';
      if (this.activePage === 'patient') return 'Patients';
      if (this.activePage === 'scorecard') return 'Scorecard';
      return '';
    },
  },
  methods: {
    setPage(id, skipSync) {
      this.activePage = id;
      this.activeDomainId = null;
      this.activeMetricId = null;
      if (id === 'overview') {
        this.activeOverviewTab = 'summary-analysis';
        this.activeDomainId = 'summary-analysis';
      } else if (id === 'attribution') {
        this.activeAttributionView = 'patient_report';
        this.activeDomainId = 'patient_report';
      } else if (id === 'utilization') {
        this.activeUtilizationView = 'cost-utilization';
        this.activeDomainId = 'cost-utilization';
      }
      if (!skipSync) this._syncQuery();
    },
    onDomainsLoaded(domains) {
      this.performanceDomains = domains;
      // Default to first domain when arriving at Performance with nothing selected
      if (this.activePage === 'performance' && !this.activeDomainId && domains.length) {
        this.activeDomainId = domains[0].id;
      }
    },
    onSideNavSelect({ type, id, parentId }) {
      if (this.activePage === 'overview') {
        this.activeOverviewTab = id;
        this.activeDomainId = id;
        this.activeMetricId = null;
        this._syncQuery();
        return;
      }
      if (this.activePage === 'attribution') {
        this.activeAttributionView = id;
        this.activeDomainId = id;
        this.activeMetricId = null;
        this._syncQuery();
        return;
      }
      if (this.activePage === 'utilization') {
        this.activeUtilizationView = id;
        this.activeDomainId = id;
        this.activeMetricId = null;
        this._syncQuery();
        return;
      }
      if (type === 'domain') {
        this.activeDomainId = id;
        this.activeMetricId = null;
      } else if (type === 'metric') {
        this.activeDomainId = parentId;
        this.activeMetricId = id;
      }
      this._syncQuery();
    },
    onSelectMetric({ domainId, metricId }) {
      this.activeDomainId = domainId;
      this.activeMetricId = metricId;
      this._syncQuery();
    },
    /**
     * Cross-tab deep link, currently emitted by the Physician Scorecard when a
     * measure name is clicked. Switches the page and pre-selects the target so
     * the user lands ON the metric instead of re-navigating (Jill's ask).
     * Written generically (tab + optional domain/metric) so other tabs can reuse it.
     */
    onNavigate({ tab, domainId, metricId }) {
      if (!tab) return;
      this.activePage = tab;
      this.activeDomainId = null;
      this.activeMetricId = null;
      if (tab === 'performance') {
        if (domainId) this.activeDomainId = domainId;
        if (metricId) this.activeMetricId = metricId;
      }
      this._syncQuery();
    },
    _syncQuery() {
      const q = { tab: this.activePage };
      if (this.activePage === 'overview') q.view = this.activeOverviewTab;
      else if (this.activePage === 'attribution') q.view = this.activeAttributionView;
      else if (this.activePage === 'utilization') q.view = this.activeUtilizationView;
      else if (this.activePage === 'performance') {
        if (this.activeMetricId) q.view = `${this.activeDomainId}:${this.activeMetricId}`;
        else if (this.activeDomainId) q.view = String(this.activeDomainId);
      }
      const current = this.$route.query;
      if (current.tab !== q.tab || current.view !== q.view) {
        this.$router.replace({ query: q }).catch(() => {});
      }
    },
    async onExport() {
      const report = this.$refs.cclfReport
      if (!report) return
      this.exporting = true
      try {
        await report.exportXlsx()
      } finally {
        this.exporting = false
      }
    },
  },
};
</script>

<style>
/* ── External editorial theme ── */
.ext-shell {
  background: #F9F6F1;
}
.ext-page {
  background: #F9F6F1;
}

/* Section header — serif editorial style */
.ext-section-number {
  font-size: 10px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  font-weight: 600;
  color: #7D7770;
  margin-bottom: 4px;
}
.ext-section-title {
  font-family: 'IBM Plex Serif', Georgia, serif;
  font-size: 22px;
  font-weight: 600;
  color: #0F0D0B;
  line-height: 1.15;
  letter-spacing: -0.015em;
  margin-bottom: 6px;
}
.ext-section-desc {
  font-size: 13px;
  color: #7D7770;
  line-height: 1.5;
  max-width: 780px;
}
.ext-section-meta {
  font-family: 'JetBrains Mono', 'SF Mono', ui-monospace, monospace;
  font-size: 11px;
  color: #7D7770;
  margin-top: 8px;
  font-variant-numeric: tabular-nums;
}

/* Metric table */
.ext-table-wrap {
  background: #FFFFFF;
  border: 1px solid #F2EEE8;
  border-radius: 8px;
  overflow: hidden;
}
.ext-table {
  width: 100%;
  border-collapse: collapse;
  font-family: 'IBM Plex Sans', -apple-system, system-ui, sans-serif;
  font-size: 12px;
}
.ext-table thead th {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #7D7770;
  padding: 8px 10px;
  border-bottom: 1px solid #F2EEE8;
  text-align: left;
}
.ext-table thead th.num {
  text-align: right;
}
.ext-th-sub {
  margin-top: 2px;
  font-size: 9.5px;
  color: #A9A29A;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.ext-table tbody td {
  padding: 5px 10px;
  border-bottom: 1px solid #F9F6F1;
  color: #221F1C;
  vertical-align: middle;
}
.ext-table tbody td.num {
  text-align: right;
  font-family: 'JetBrains Mono', 'SF Mono', ui-monospace, monospace;
  font-variant-numeric: tabular-nums;
  font-feature-settings: "tnum", "zero";
  font-size: 11.5px;
  font-weight: 500;
  letter-spacing: -0.01em;
}
.ext-table tbody td.metric-name {
  font-weight: 500;
  color: #221F1C;
  padding-left: 16px;
}
.ext-table tbody tr:last-child td {
  border-bottom: none;
}
.ext-table tbody tr:nth-child(even) td {
  background: #fafaf7;
}
.ext-table tbody tr:hover td {
  background: #FFF4EC;
}

/* Group header row */
.ext-table tbody tr.group-header td {
  font-size: 9.5px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #7D7770;
  padding: 10px 10px 10px 16px;
  border-bottom: 1px solid #F2EEE8;
  background: none;
  vertical-align: middle;
}

/* Hero stat block */
.ext-hero {
  background: #FFFFFF;
  border: 1px solid #F2EEE8;
  border-radius: 8px;
  padding: 18px 20px;
  margin-bottom: 24px;
}
.ext-hero-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #8E8F90;
  margin-bottom: 6px;
}
.ext-hero-value {
  font-family: 'JetBrains Mono', 'SF Mono', ui-monospace, monospace;
  font-feature-settings: "tnum", "zero";
  font-size: 34px;
  font-weight: 600;
  color: #0F0D0B;
  line-height: 1;
  letter-spacing: -0.02em;
}
.ext-hero-unit {
  font-family: 'IBM Plex Sans', system-ui, sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: #A9A29A;
  margin-left: 6px;
  margin-left: 6px;
}
.ext-hero-sub {
  font-size: 12px;
  color: #A5A5A6;
  margin-top: 8px;
}

/* Change tag */
.ext-change {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 12px;
  font-weight: 600;
}
.ext-change.positive { color: #389A6C; }
.ext-change.negative { color: #D53D3D; }

/* ── Shared component overrides for external editorial theme ── */

/* Shared components — white card background on table wrappers */
.ext-page .border.border-gray-200.rounded-lg {
  background: #FFFFFF;
  border-color: #F2EEE8 !important;
}

/* Force editorial font stack on entire external page */
.ext-page,
.ext-page * {
  font-family: 'IBM Plex Sans', system-ui, -apple-system, sans-serif;
  font-size: 12px;
}
.ext-page .font-mono,
.ext-page [class*="mono"],
.ext-page code,
.ext-page .tabular-nums {
  font-family: 'JetBrains Mono', 'SF Mono', ui-monospace, monospace !important;
}
</style>
